import React, { useMemo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip } from "@fluentui/react-components";
import {
    ProjectIdCell,
    renderCellContent,
    makeDecimalAnalystCellHelperStaffer,
    EnergyBankerEmptyState,
    EnergyBankerNoDealOnFilterState,
    EnergyBankerNoDealOnSearchState,
    EnergyStafferNoDealOnFilterState,
    EnergyBankerEmptyStateStaffer,
    getAccordionRowRender,
    useGroupToggle,
    getFlattenedData,
    getFilteredSortedData,
    saveDetailStateToStorage,
} from "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";
import {
    renderlastUpdatedTooltipHeader,
    formatLastUpdatedDate,
    renderTooltipHeader,
    createColumnMenu,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { getLastUpdatedDateColorWeeks } from "../../../dashboard-util/dashboard-utils";
import  { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { LoginContext } from "../../../../../services/api/authentication-api";
import "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log.scss";

const EnergyAnalystDetailGrid = ({
    data = [],
    onItemChange,
    detailState,
    onDetailStateChange,
    resetDataState,
 }) => {
    const user = useContext(LoginContext);
    const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
    const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
    const { groupState, toggleGroup, setGroupState } = useGroupToggle();
    const flattenedData = useMemo(() => getFlattenedData(data), [data]);
    const filteredData = useMemo(
        () => getFilteredSortedData(flattenedData, detailState.filter, detailState.sort),
    [flattenedData, detailState.filter, detailState.sort]);
    const visibleData = useMemo(() => filteredData, [filteredData]);
    const visibleLength = useMemo(() => visibleData?.length ?? 0, [visibleData]);

    const columnMenu = useMemo(() => createColumnMenu(flattenedData), [flattenedData]);
    const columnIsFiltered = useCallback((fieldName) => isFilteredColumn(fieldName, detailState.filter), [detailState.filter]);

    const isEmpty = visibleData?.length === 0;
    const finalGridData = isEmpty
        ? [{
            isEmptyRow: true,
            showSearchEmptyState: false,
            isFilterEmptyState: (detailState.filter?.filters?.length ?? 0) > 0,
          }]
        : visibleData;

    const AccordionRowRender =
        useMemo(() => getAccordionRowRender(filteredData, groupState, toggleGroup, 9), [filteredData, groupState]);

    const makeDecimalCell = useMemo(() => makeDecimalAnalystCellHelperStaffer(visibleLength, user?.userID), [visibleLength, user?.userID]);
    const LastWeekCell = useMemo(
        () => makeDecimalCell(),
        [makeDecimalCell]
    );
    const WeekendCell = useMemo(
        () => makeDecimalCell(),
        [makeDecimalCell]
    );
    const CurrentWeekHoursCell = useMemo(
        () => makeDecimalCell(),
        [makeDecimalCell]
    );

    return (
        <div className="detail-grid-scroll-container">
            <Grid
                data={finalGridData}
                sortable={true}
                sort={detailState.sort}
                filter={detailState.filter}
                onSortChange={(e) => {
                    onDetailStateChange({ ...detailState, sort: e.sort });
                    saveDetailStateToStorage(user.userID, { ...detailState, sort: e.sort }, "", "analystDealsTimeLog");
                    setGroupState({ ...groupState });
                }}
                onFilterChange={(e) => {
                    onDetailStateChange({ ...detailState, filter: e.filter });
                    saveDetailStateToStorage(user.userID, { ...detailState, filter: e.filter }, "", "analystDealsTimeLog");
                }}
                editable="true"
                editField="inEdit"
                onItemChange={onItemChange}
                rowRender={AccordionRowRender}
                navigable={true}
                keyboardNavigation={true}
                dataItemKey="ProjectId"
                resizable={true}
            >
                <Column
                    field="ProjectId"
                    editable={false}
                    title={renderTooltipHeader("#")}
                    filter="text"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("ProjectId") ? "filtered" : "" }
                    cell={(props) =>
                        props.dataItem.isEmptyRow
                        ?   <td colSpan={9} className="text-center k-table-td">
                                { props.dataItem.isFilterEmptyState
                                    ?   <EnergyBankerNoDealOnFilterState resetDataState={resetDataState} />
                                    :   <EnergyBankerEmptyStateStaffer canCreate={canCreate} />
                                }
                            </td>
                        :   <ProjectIdCell {...props} canEdit={canEdit && props.dataItem.IsProjectMember} isLive={props.dataItem.GroupName == "Live"} />
                    }
                />
                <Column
                    field="ProjectName"
                    title={renderTooltipHeader("Deal Name")}
                    editable={false}
                    filter="text"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("ProjectName") ? "filtered" : "" }
                    cell={(props) =>
                        renderCellContent(visibleData?.length ?? 0, null, (
                            <td className="background-white k-table-td">
                                <Tooltip content={props.dataItem.ProjectName} relationship="label">
                                    <span className="truncate-text" data-testid="data-project-name">{props.dataItem.ProjectName}</span>
                                </Tooltip>
                            </td>
                        ))
                    }
                />
                <Column
                    field="Company"
                    title={renderTooltipHeader("Company")}
                    editable={false}
                    filter="text"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("Company") ? "filtered" : "" }
                    cell={(props) =>
                        renderCellContent(visibleData?.length ?? 0, null, (
                            <td className="background-white k-table-td">
                                <Tooltip content={props.dataItem.Company} relationship="label">
                                    <span className="truncate-text" data-testid="data-company-name">{props.dataItem.Company ?? "—"}</span>
                                </Tooltip>
                            </td>
                        ))
                    }
                />
                <Column
                    field="LastWeekHours"
                    title={renderTooltipHeader("Last Wk Actual Hrs")}
                    editor="numeric"
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("LastWeekHours") ? "filtered" : "" }
                    cell={LastWeekCell}
                />
                <Column
                    field="WeekendHours"
                    title={renderTooltipHeader("Last Wknd Hrs")}
                    editor="numeric"
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("WeekendHours") ? "filtered" : "" }
                    cell={WeekendCell}
                />
                <Column
                    field="ActualTotalHours"
                    title={renderTooltipHeader("Actual Total Hrs")}
                    editable={false}
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("ActualTotalHours") ? "filtered" : "" }
                    cell={(props) =>
                        renderCellContent(
                            visibleData?.length ?? 0,
                            null,
                            <td className="background-white k-table-td">
                                {props.dataItem.ActualTotalHours ?? "—"}
                            </td>
                        )
                    }
                />
                <Column
                    field="PriorWeekEstimate"
                    title={renderTooltipHeader("Prior Week Est.")}
                    editable={false}
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("PriorWeekEstimate") ? "filtered" : "" }
                    cell={(props) =>
                        renderCellContent(
                            visibleData?.length ?? 0,
                            null,
                            <td className="background-white k-table-td">
                                {   props.dataItem?.PriorWeekEstimate > 0
                                    ? props.dataItem?.PriorWeekEstimate
                                    :  "—"
                                }
                            </td>
                        )
                    }
                />
                <Column
                    field="NextWeekHours"
                    title={renderTooltipHeader("Current Est. of Hrs")}
                    editor="numeric"
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("NextWeekHours") ? "filtered" : "" }
                    cell={CurrentWeekHoursCell}
                />
                <Column
                    field="LastUpdatedString"
                    title={renderlastUpdatedTooltipHeader("Last Updated")}
                    editable={false}
                    filter="date"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("LastUpdatedString") ? "filtered" : "" }
                    cell={(props) => (
                        renderCellContent(visibleData?.length ?? 0, null, (
                            <td className="background-white k-table-td"
                                style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdatedString) }}
                            >
                                <span>{formatLastUpdatedDate(props.dataItem.LastUpdatedString) ?? "—"}</span>
                            </td>
                        ))
                    )}
                />
            </Grid>
        </div>
    );
};

EnergyAnalystDetailGrid.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            GroupName: PropTypes.string,
            Level: PropTypes.string,
            ProjectId: PropTypes.number,
            ProjectIdString: PropTypes.string,
            TaskId: PropTypes.number,
            ProjectName: PropTypes.string,
            Company: PropTypes.string,
            LastWeekHours: PropTypes.number,
            WeekendHours: PropTypes.number,
            TotalHours: PropTypes.number,
            PriorWeekEstimate: PropTypes.number,
            CurrentWeekHours: PropTypes.number,
            LastUpdated: PropTypes.string,
            IsOverdue: PropTypes.bool,
            IsProjectMember: PropTypes.bool,
        })
    ).isRequired,
    onItemChange: PropTypes.func,
    canEdit: PropTypes.bool,
    canCreate: PropTypes.bool,
    detailState: PropTypes.shape({
        sort: PropTypes.arrayOf(PropTypes.object),
        filter: PropTypes.object,
    }).isRequired,
    onDetailStateChange: PropTypes.func,
    resetDataState: PropTypes.func,
};

export default EnergyAnalystDetailGrid;
